import React from 'react';
import type { HilChatMessageRendererProps } from '../settings/HilChatWindowSettings';

function formatTimestamp(sentAt: string): string {
  const date = new Date(sentAt);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/** "Binoy Jose" -> "BJ", "Slack" -> "S", "  " / "" -> null (caller falls back to the generic
 * per-sender initial). Takes the first letter of up to the first two whitespace-separated words —
 * deliberately simple (no locale-aware name-part logic) since this is a two-character avatar hint,
 * not an identity display. */
function initialsFromName(name: string | null | undefined): string | null {
  const parts = name?.trim().split(/\s+/).filter(Boolean) ?? [];
  if (parts.length === 0) return null;
  const initials = parts.slice(0, 2).map((p) => p[0]!.toUpperCase()).join('');
  return initials || null;
}

export function HilChatTextMessage({ message, settings, onRetry }: HilChatMessageRendererProps): React.JSX.Element {
  const showAvatar = settings.visibility?.avatars !== false;
  // Only rendered when both the app opts in AND a real timestamp exists — system
  // messages carry sentAt: null (no backend-authored per-turn time), so they never
  // show a fabricated one even when this setting is on.
  const showTimestamp = settings.visibility?.timestamps !== false && !!message.sentAt;

  return (
    <div className={`hil-chat-bubble hil-chat-bubble--${message.sender}`}>
      {showAvatar && (
        <div className="hil-chat-bubble__avatar" aria-hidden="true">
          {initialsFromName(message.senderName) ?? (message.sender === 'user' ? 'U' : 'S')}
        </div>
      )}
      <div className="hil-chat-bubble__content">
        <p className="hil-chat-bubble__text">{message.promptMessage}</p>
        {showTimestamp && (
          <time className="hil-chat-bubble__timestamp" dateTime={message.sentAt ?? undefined}>
            {formatTimestamp(message.sentAt as string)}
          </time>
        )}
        {message.status === 'pending' && (
          <span className="hil-chat-bubble__status hil-chat-bubble__status--pending">Sending…</span>
        )}
        {message.status === 'failed' && (
          <div className="hil-chat-bubble__status hil-chat-bubble__status--failed">
            <span>Failed to send.</span>
            {onRetry && (
              <button type="button" className="hil-chat-bubble__retry" onClick={onRetry}>
                Retry
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
